import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  // Users,
  Heart,
  Target,
  Zap,
  Shield,
  Star,
  // TrendingUp,
  CheckCircle,
  Phone,
  ArrowRight,
  Quote,
  Sparkles,
  // ThumbsUp,
  Car,
  // Wrench,
  // BadgeCheck,
  // Trophy,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import "./About.css";

// Scroll reveal hook
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    document.querySelectorAll(".scroll-reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

// Counter animation hook
// function useCounter(end: number, duration: number = 2000) {
//   const [count, setCount] = useState(0);
//   const [isVisible, setIsVisible] = useState(false);
//   const ref = useRef<HTMLDivElement>(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting && !isVisible) {
//           setIsVisible(true);
//         }
//       },
//       { threshold: 0.5 },
//     );

//     if (ref.current) {
//       observer.observe(ref.current);
//     }

//     return () => observer.disconnect();
//   }, [isVisible]);

//   useEffect(() => {
//     if (!isVisible) return;

//     let startTime: number;
//     const animate = (currentTime: number) => {
//       if (!startTime) startTime = currentTime;
//       const progress = Math.min((currentTime - startTime) / duration, 1);
//       setCount(Math.floor(progress * end));
//       if (progress < 1) {
//         requestAnimationFrame(animate);
//       }
//     };
//     requestAnimationFrame(animate);
//   }, [isVisible, end, duration]);

//   return { count, ref };
// }

function About() {
  useScrollReveal();

  // Counter stats
  // const yearsInBusiness = useCounter(19, 2000);
  // const vehiclesSold = useCounter(15000, 2500);
  // const happyCustomers = useCounter(12000, 2500);
  // const teamMembers = useCounter(45, 1500);

  // Core values
  const coreValues = [
    {
      icon: Heart,
      title: "All Credit Welcome",
      description:
        "We help customers with bad credit, low credit, no credit, and new credit situations in Canada.",
      color: "from-red-500 to-pink-500",
    },
    {
      icon: Shield,
      title: "Multiple Lenders",
      description:
        "We work with multiple lenders to help find financing options that fit your needs.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Award,
      title: "Steady Income Support",
      description:
        "If you have a steady income or full-time job, our team will work hard to help you get approved.",
      color: "from-yellow-500 to-orange-500",
    },
    {
      icon: Zap,
      title: "Reliable Vehicle Focus",
      description:
        "Our goal is to help you get into a dependable vehicle and move forward with confidence.",
      color: "from-green-500 to-emerald-500",
    },
  ];

  // Milestones
  // const milestones = [
  //   {
  //     year: "2005",
  //     title: "Founded",
  //     description: "Big Boys Motor Club established in Langley, BC",
  //   },
  //   {
  //     year: "2010",
  //     title: "Expansion",
  //     description: "Expanded showroom to accommodate growing inventory",
  //   },
  //   {
  //     year: "2015",
  //     title: "Award Winner",
  //     description: "Recognized as Best Used Car Dealership in BC",
  //   },
  //   {
  //     year: "2020",
  //     title: "Digital Transformation",
  //     description: "Launched online showroom and virtual test drives",
  //   },
  //   {
  //     year: "2024",
  //     title: "15,000th Sale",
  //     description: "Celebrated milestone of 15,000 vehicles sold",
  //   },
  // ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="/images/about-showroom.jpg"
            alt="Our showroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-transparent" />
        </div>

        {/* Animated Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-red-500/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl scroll-reveal">
            <Badge className="mb-6 bg-gradient-to-r from-red-600 to-red-500 text-white border-0 px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              All Credit Welcome
            </Badge>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              CREDIT{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-yellow-400">
                CHALLENGES?
              </span>
              <br />
              WE'VE GOT YOU COVERED.
            </h1>

            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              Whether you have bad credit, low credit, or are new to Canada with
              no credit history, getting approved for a vehicle may still be
              possible. At Big Boys MotorClub, we work with multiple lenders to
              help customers with all types of credit situations.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/#financing"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-red-700 transition-all hover:shadow-lg hover:shadow-red-500/30"
              >
                <Car className="w-5 h-5" />
                Apply for Financing
                <ArrowRight className="w-5 h-5" />
              </Link>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                <Phone className="w-5 h-5" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="relative -mt-20 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 scroll-reveal">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center" ref={yearsInBusiness.ref}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  {yearsInBusiness.count}+
                </div>
                <p className="text-gray-600 font-medium">Years in Business</p>
              </div>
              <div className="text-center" ref={vehiclesSold.ref}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  {vehiclesSold.count.toLocaleString()}+
                </div>
                <p className="text-gray-600 font-medium">Vehicles Sold</p>
              </div>
              <div className="text-center" ref={happyCustomers.ref}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  {happyCustomers.count.toLocaleString()}+
                </div>
                <p className="text-gray-600 font-medium">Happy Customers</p>
              </div>
              <div className="text-center" ref={teamMembers.ref}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">
                  {teamMembers.count}+
                </div>
                <p className="text-gray-600 font-medium">Team Members</p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Main Content */}
      <main className="pt-28 pb-16">
        {/* Our Story Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="scroll-reveal relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/about-customer.jpg"
                  alt="Customer satisfaction"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
              </div>

              {/* Floating Badge */}
              {/* <div className="absolute -bottom-6 -right-6 bg-red-600 rounded-2xl p-6 shadow-xl hidden md:block">
                <div className="flex items-center gap-4">
                  <Trophy className="w-10 h-10 text-white" />
                  <div>
                    <p className="text-white font-bold text-lg">
                      Award Winning
                    </p>
                    <p className="text-red-200 text-sm">Best Dealership 2024</p>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Content */}
            <div className="scroll-reveal">
              <Badge className="mb-4 bg-red-100 text-red-700 border-0">
                <Target className="w-4 h-4 mr-2" />
                Why Customers Choose Us
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Financing Help for{" "}
                <span className="text-red-600">All Credit Situations</span>
              </h2>

              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  At Big Boys MotorClub, we understand that every customer’s
                  financial situation is different. Whether you have bad credit,
                  low credit, no credit, or are new to Canada, our team is here
                  to help you explore financing options.
                </p>
                <p>
                  We work with multiple lenders to support customers from many
                  different credit backgrounds. If you have a steady income or
                  full-time job, we will work hard to help find a financing
                  solution that fits your situation.
                </p>
                <p>
                  Our goal is simple: help you get approved, get you into a
                  reliable vehicle, and help you move forward with confidence.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Bad Credit Welcome</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Low Credit Welcome</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>No Credit / New to Canada</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="bg-gray-900 py-24 mb-24 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600 rounded-full filter blur-3xl" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 scroll-reveal">
              <Badge className="mb-4 bg-white/10 text-white border-0">
                <Heart className="w-4 h-4 mr-2" />
                How We Help
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                What <span className="text-red-500">Makes Us Different</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                We focus on real financing help for customers in all kinds of
                credit situations.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="scroll-reveal bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all group"
                >
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                  >
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Team Section */}
        {/* <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            <div className="scroll-reveal order-2 lg:order-1">
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">
                <Users className="w-4 h-4 mr-2" />
                Our Team
              </Badge>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Meet the <span className="text-red-600">People</span> Behind Our
                Success
              </h2>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our team of 45+ dedicated professionals is what sets us apart.
                From our knowledgeable sales consultants to our expert service
                technicians, every member of the Big Boys Motor Club family is
                committed to delivering exceptional service.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BadgeCheck className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Industry Experts
                    </h4>
                    <p className="text-gray-600">
                      Average 10+ years of automotive experience
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ThumbsUp className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Customer Focused
                    </h4>
                    <p className="text-gray-600">
                      Trained to put your needs first
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Certified Technicians
                    </h4>
                    <p className="text-gray-600">
                      Factory-trained service professionals
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="/careers"
                className="inline-flex items-center gap-2 text-red-600 font-semibold hover:underline"
              >
                Join Our Team
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

        
            <div className="scroll-reveal order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/about-team.jpg"
                  alt="Our team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section> */}

        {/* Timeline Section */}
        {/* <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-24 mb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 scroll-reveal">
              <Badge className="mb-4 bg-yellow-100 text-yellow-700 border-0">
                <TrendingUp className="w-4 h-4 mr-2" />
                Our Journey
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Milestones That <span className="text-red-600">Define Us</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                A timeline of our growth and achievements over the years
              </p>
            </div>

            <div className="relative scroll-reveal">
              
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-red-500 to-yellow-500 rounded-full" />

              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row items-center gap-8 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    
                    <div
                      className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}
                    >
                      <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <span className="text-3xl font-bold text-red-600">
                          {milestone.year}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900 mt-2">
                          {milestone.title}
                        </h3>
                        <p className="text-gray-600 mt-2">
                          {milestone.description}
                        </p>
                      </div>
                    </div>

                    
                    <div className="w-6 h-6 bg-red-600 rounded-full border-4 border-white shadow-lg z-10 flex-shrink-0" />

                   
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section> */}

        {/* Testimonials Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="text-center mb-16 scroll-reveal">
            <Badge className="mb-4 bg-green-100 text-green-700 border-0">
              <Star className="w-4 h-4 mr-2" />
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-red-600">Customers</span> Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it — hear from the people who matter
              most
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  "I thought getting approved would be difficult because of my credit, but the team made the process simple and treated me with respect from start to finish.",
                author: "David Martin",
                role: "Financing Customer",
                rating: 5,
              },
              {
                quote:
                  "As someone new to Canada, I was worried about having no credit history. Big Boys MotorClub helped me understand my options and made everything feel much easier.",
                author: "Sarah Dhillon",
                role: "New to Canada Buyer",
                rating: 5,
              },
              {
                quote:
                  "I had a steady job but low credit, and other places turned me away. Here, they worked hard to find a solution and helped me get into a reliable vehicle.",
                author: "Aman Brar",
                role: "Approved Customer",
                rating: 5,
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="scroll-reveal bg-white rounded-2xl shadow-lg p-8 border-t-4 border-red-600 relative"
              >
                <Quote className="absolute top-4 right-4 w-10 h-10 text-red-100" />
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-red-600">
                      {testimonial.author
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">
                      {testimonial.author}
                    </p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl" />
            </div>

            <div className="relative z-10 px-8 py-16 md:px-16 md:py-20 text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Experience the{" "}
                <span className="text-yellow-300">Difference?</span>
              </h2>

              <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
                Bad credit, low credit, no credit, or new to Canada — if you
                have a steady income or full-time job, we may be able to help
                you get approved.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/car-inventory"
                  className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all"
                >
                  <Car className="w-5 h-5" />
                  Browse Inventory
                </Link>

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
                >
                  <Phone className="w-5 h-5" />
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default About;
